import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight, Plus } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import Card from '../../../components/Card'
import BannerImage from '../../../components/BannerImage'
import LoginButtons from '../../../components/LoginButtons'
import TeamCard from '../../Home/components/landing/TeamCard'
import TroubleshootingModal from '../../UserDashboard/components/TroubleshootingModal'
import {
  usePageInfo,
  useGuildDetails,
  useGuildMembers,
} from '../../../config/queries'
import { useUpdateOnboardingStep } from '../../../config/mutations'
import { useRefreshData } from '../../../hooks/useRefreshData'
import { hasAdminAccessToAllTeams } from '../../../config/permissions'
import { refreshPersonalCharacters } from '../../../api/user'
import { updateGuild } from '../../../api/guild'
import useReferenceStore from '../../../state/useReferenceStore'
import OnboardingStepper from './OnboardingStepper'
import OnboardingWizard from './OnboardingWizard'
import OnboardingTeamProvider from './OnboardingTeamProvider'
import ReviewStep from './steps/ReviewStep'
import { useOnboardingSteps } from './onboardingSteps'

const GuildOnboarding = ({ basePath }: { basePath: string }) => {
  const { t } = useTranslation()
  const { getReference } = useReferenceStore()
  const guildReference = getReference<wowaudit.GuildReference>(basePath)
  const { data: guild } = useGuildDetails(guildReference?.id)
  const stepIds = useOnboardingSteps()

  const {
    data: { user },
  } = usePageInfo()

  const { data: guildMembers, isPending: membersPending } = useGuildMembers(
    guild?.id,
  )

  const reduceMotion = useReducedMotion()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const furthestStepRef = useRef(0)

  const stepParam = searchParams.get('step') ?? ''
  const completed = stepParam === 'complete'
  const stepIndex = stepIds.indexOf(stepParam as typeof stepIds[number])
  const inWizard = stepIndex >= 0
  const teamParam = searchParams.get('team')
  const updateOnboardingStep = useUpdateOnboardingStep(
    teamParam ? Number(teamParam) : 0,
  )

  const hasCharacterInGuild = useMemo(() => {
    if (!user || !guildMembers) return false
    const memberIds = new Set(guildMembers.map((member) => member.id))
    return user.characterReferences.some((character) =>
      memberIds.has(character.id),
    )
  }, [user, guildMembers])

  const isFirstTeamSetup = guild?.teamReferences.length === 0

  const { refreshing: refreshingCharacters, refreshData: refreshCharacters } =
    useRefreshData({
      retriever: refreshPersonalCharacters,
      queryToInvalidate: ['pageInfo'],
      callback: () => {
        if (!guild) return
        queryClient.invalidateQueries({
          queryKey: ['guildMembers', { id: guild.id }],
        })
        queryClient.invalidateQueries({
          queryKey: ['guild', { path: guild.path }],
        })
      },
    })

  const { refreshing: refreshingGuild, refreshData: refreshGuild } =
    useRefreshData({
      retriever: updateGuild,
      id: guild?.id,
      callback: () => refreshCharacters(),
    })

  if (!guild) {
    return <Progress className='h-1' indeterminate />
  }

  const visibleTeams = guild.teamReferences.filter(
    (team) => team.visibleInGuild,
  )
  const hasExistingTeams = visibleTeams.length > 0
  const atTeamLimit = guild.teamReferences.length >= guild.teamLimit
  const canCreateTeam = hasAdminAccessToAllTeams(guild)
  const createdTeam = teamParam
    ? guild.teamReferences.find((team) => String(team.id) === teamParam)
    : undefined

  const persistedStepIndex = createdTeam?.onboardingStep
    ? stepIds.indexOf(createdTeam.onboardingStep as typeof stepIds[number])
    : -1

  const goToStep = (index: number, teamId?: number) => {
    const team = teamId ?? teamParam

    if (index > stepIndex) {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' })
    }

    if (index >= stepIds.length) {
      const params: Record<string, string> = { step: 'complete' }
      if (team) params.team = String(team)
      setSearchParams(params, { replace: true })
      return
    }

    const clamped = Math.min(Math.max(index, 0), stepIds.length - 1)
    const params: Record<string, string> = { step: stepIds[clamped] }
    if (team) params.team = String(team)
    setSearchParams(params, { replace: true })

    if (teamParam) {
      const furthest = Math.max(furthestStepRef.current, persistedStepIndex)
      if (clamped > furthest) {
        furthestStepRef.current = clamped
        updateOnboardingStep.mutate(stepIds[clamped])
      }
    }
  }

  const onFinish = () => {
    if (!createdTeam) {
      navigate(guild.defaultPath)
      return
    }

    updateOnboardingStep.mutate(null, {
      onSuccess: (updatedTeam) => {
        queryClient.setQueryData(
          ['team', { path: createdTeam.path }],
          updatedTeam,
        )
        queryClient.invalidateQueries({
          queryKey: ['guild', { path: guild.path }],
        })
        navigate(`${createdTeam.path}/audit`)
      },
    })
  }

  const renderBody = () => {
    if (!user) {
      return (
        <div className='flex flex-col items-center gap-6 text-center'>
          <div className='flex flex-col items-center gap-2'>
            <h2 className='text-2xl font-semibold'>Log in to continue</h2>
            <p className='max-w-md text-muted-foreground'>
              You must own a character in {guild.name} to continue setting up.
            </p>
          </div>
          <LoginButtons excludeGoogle />
        </div>
      )
    }

    if (isFirstTeamSetup && !hasCharacterInGuild) {
      return (
        <div className='flex flex-col items-center gap-6 text-center'>
          <Alert variant='warning' className='text-left'>
            <AlertTitle>
              We can&apos;t find any of your characters in this guild
            </AlertTitle>
            <AlertDescription>
              A guild can only be set up by one of its members. Make sure you
              are logged in with the right Blizzard account and that your
              characters are up to date.
            </AlertDescription>
          </Alert>
          <div className='flex gap-2'>
            <Button
              variant='outline'
              onClick={() => refreshGuild()}
              loading={refreshingCharacters || refreshingGuild}
              spinner='blizzard'
            >
              Refresh
            </Button>
            <Button variant='outline' onClick={() => setHelpModalOpen(true)}>
              Troubleshooting
            </Button>
          </div>
          {helpModalOpen ? (
            <TroubleshootingModal
              inProfile={false}
              setOpen={setHelpModalOpen}
            />
          ) : null}
        </div>
      )
    }

    if (!isFirstTeamSetup && !hasAdminAccessToAllTeams(guild)) {
      return (
        <Alert variant='warning' className='text-left'>
          <AlertTitle>
            You don&apos;t have admin access to this guild
          </AlertTitle>
          <AlertDescription>
            Creating a new team requires admin access to all existing teams in
            this guild.
          </AlertDescription>
        </Alert>
      )
    }

    if (completed) {
      const reviewStep = (
        <ReviewStep
          createdTeam={createdTeam}
          onFinish={onFinish}
          onBack={() => goToStep(stepIds.length - 1)}
        />
      )

      if (!createdTeam) return reviewStep

      return (
        <OnboardingTeamProvider
          guild={guild}
          guildReference={guildReference}
          createdTeam={createdTeam}
        >
          {reviewStep}
        </OnboardingTeamProvider>
      )
    }

    if (!inWizard) {
      if (hasExistingTeams) {
        return (
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col items-center gap-2 text-center'>
              <h2 className='text-2xl font-semibold'>Welcome back</h2>
              <p className='max-w-md text-muted-foreground'>
                {guild.name} already has teams set up. Open one to manage it
                {atTeamLimit ? '.' : ', or create another team to get started.'}
              </p>
            </div>

            <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
              {visibleTeams.map((team) => (
                <TeamCard
                  key={team.id}
                  team={team}
                  continueSetup={!!team.onboardingStep}
                />
              ))}
            </div>

            {atTeamLimit ? (
              <p className='text-center text-sm text-muted-foreground'>
                Your guild has reached its limit of {guild.teamLimit} team
                {guild.teamLimit > 1 ? 's' : ''}.
              </p>
            ) : canCreateTeam ? (
              <button
                type='button'
                onClick={() => goToStep(0)}
                className='group inline-flex items-center justify-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground'
              >
                <Plus className='h-4 w-4' />
                Create a new team
                <ArrowRight className='h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5' />
              </button>
            ) : null}
          </div>
        )
      }

      return <Navigate to={{ search: `step=${stepIds[0]}` }} replace />
    }

    return (
      <OnboardingWizard
        guild={guild}
        guildReference={guildReference}
        stepIndex={stepIndex}
        createdTeam={createdTeam}
        onTeamCreated={(team) => goToStep(1, team.id)}
        onFinish={onFinish}
        goNext={() => goToStep(stepIndex + 1)}
        goBack={() => goToStep(stepIndex - 1)}
      />
    )
  }

  return (
    <div className='mx-auto flex max-w-3xl flex-col gap-8 py-8'>
      <motion.div
        initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className='flex items-center gap-3'
      >
        <BannerImage
          size='small'
          faction={guild.faction}
          fileName={guild.bannerFilename}
          className='shrink-0 rounded-md'
        />
        <div className='flex flex-col'>
          <span className='text-sm text-muted-foreground'>Setting up</span>
          <h1 className='text-xl font-semibold'>{guild.name}</h1>
        </div>
      </motion.div>

      <OnboardingStepper
        labels={stepIds.map((id) => t(`onboarding.steps.${id}`))}
        currentIndex={completed ? stepIds.length : inWizard ? stepIndex : 0}
      />

      {user && isFirstTeamSetup && membersPending ? (
        <Skeleton className='h-64' />
      ) : (
        <Card className='p-6 sm:p-8'>{renderBody()}</Card>
      )}
    </div>
  )
}

export default GuildOnboarding
